package com.ijoin.data.model

import com.ijoin.data.model.serialization.InstantIso8601Serializer
import kotlinx.datetime.Instant
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

@Serializable
data class Event(
    val id: String,
    val title: String,
    val description: String? = null,
    val organizerId: String? = null,
    val organizerName: String? = null,
    val organizerPlatformFeePercent: Double? = null,
    val locationName: String? = null,
    val locationAddress: String? = null,
    val locationCity: String? = null,
    val locationCountry: String? = null,
    val locationLatitude: Double? = null,
    val locationLongitude: Double? = null,
    @Serializable(with = InstantIso8601Serializer::class)
    val eventDate: Instant? = null,
    @Serializable(with = InstantIso8601Serializer::class)
    val eventEndDate: Instant? = null,
    val durationMinutes: Int? = null,
    val coverImageUrl: String? = null,
    val imageUrls: List<String>? = null,
    val videoUrls: List<String>? = null,
    val mobileImageUrls: List<String>? = null,
    val mediaStorageFolder: String? = null,
    val priceTiers: List<PriceTier>? = null,
    val category: String? = null,
    val tags: List<String>? = null,
    val availableTickets: Int? = null,
    val status: EventStatus? = null,
    val previousStatus: EventStatus? = null,
    @Serializable(with = InstantIso8601Serializer::class)
    val cancelledAt: Instant? = null,
    val cancellationReason: String? = null,
    val favoritesCount: Int? = null,
    val maxCapacity: Int? = null,
    val ticketPrice: Double? = null,
    val currency: String? = null,
    val scheduleType: String? = null,
    val eventTime: String? = null,
    val timeZone: String? = null,
    val selectedDaysOfWeek: List<Int>? = null,
    val selectedDaysOfMonth: List<Int>? = null,
    val weeklyTime: String? = null,
    val weeklyEndTime: String? = null,
    val monthlyTime: String? = null,
    val minSlotsPerBooking: Int? = null,
    val slotDurationMinutes: Int? = null,
    val slotDayConfigurations: List<DaySlotConfiguration>? = null,
    // New fields
    @SerialName("online")
    val isOnline: Boolean? = null,
    val onlineLink: String? = null,
    val isRecurring: Boolean? = null,
    val recurrenceType: EventRecurrenceType? = null,
    val recurrenceDaysOfWeek: List<Int>? = null, // 0-6 (Monday-Sunday)
    val recurrenceDaysOfMonth: List<Int>? = null, // 1-31
    val hiddenInfo: String? = null, // Information available after ticket purchase
    @SerialName("enableCarpool")
    val carpoolEnabled: Boolean? = null,
    val carpoolPaymentPerPerson: Double? = null,
    val carpoolFree: Boolean? = null,
    val mediaUrls: List<String>? = null, // Photo/video URLs
    val groupChatEnabled: Boolean? = null,
    val groupChatThreadId: Long? = null,
    val requireContactInfo: Boolean? = null,
    @Serializable(with = InstantIso8601Serializer::class)
    val createdAt: Instant? = null,
    /**
     * Custom HTML landing content for the event page.
     * Displayed above the booking section.
     * Can include embedded CSS and base64 images.
     */
    val landingHtml: String? = null,
    /**
     * If true, use custom landing page instead of standard event sections.
     * When enabled, dress code, location, description, and reviews blocks are hidden.
     */
    val enableLandingPage: Boolean? = null
)

@Serializable
data class PriceTier(
    val name: String,
    val price: Double, // Using Double for simplicity in Kotlin Multiplatform
    val priority: Int,
    val availableTickets: Int? = null,
    val description: String? = null
)

@Serializable
enum class EventStatus {
    DRAFT,
    PUBLISHED,
    ONGOING,
    COMPLETED,
    CANCELLED,
    DELETED
}

@Serializable
enum class EventRecurrenceType {
    DAYS_OF_WEEK,
    DAYS_OF_MONTH
}

@Serializable
data class DaySlotConfiguration(
    val dayOfWeek: Int, // 0-6 or 1-7
    val startTime: String, // HH:mm format
    val endTime: String // HH:mm format
)
